import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminContentHealth, adminDbCollections, adminDbDocuments } from "@/lib/api";
import { AlertTriangle, Database, CheckCircle2, ImageOff, FileWarning, Layers, Search } from "lucide-react";

function HealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminContentHealth().then(setData).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="py-16 text-center text-ink-3">…</p>;
  if (!data) return <p className="py-16 text-center text-ink-3">Could not load content health.</p>;

  const dupSlugs = data.duplicate_slugs || [];
  const dupGroups = {};
  (data.duplicates || []).forEach((d) => { (dupGroups[d.slug || "(no slug)"] ||= []).push(d); });

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: "Total items", value: data.total, icon: Layers, tone: "text-ink" },
          { label: "Duplicate addresses", value: dupSlugs.length, icon: AlertTriangle, tone: dupSlugs.length ? "text-red-600" : "text-emerald-600" },
          { label: "Missing image", value: (data.missing_image || []).length, icon: ImageOff, tone: "text-amber-600" },
          { label: "Drafts", value: (data.drafts || []).length, icon: FileWarning, tone: "text-ink-2" },
        ].map((s) => (
          <div key={s.label} className="border border-line bg-surface p-4">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-ink-3">{s.label}</span><s.icon className={`h-4 w-4 ${s.tone}`} /></div>
            <p className={`mt-2 font-serif-latin text-3xl ${s.tone}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <section>
        <h2 className="mb-2 flex items-center gap-2 font-serif-latin text-xl text-ink"><AlertTriangle className="h-5 w-5 text-red-500" /> Duplicate web addresses</h2>
        <p className="mb-4 text-sm text-ink-3">When two items share the same address (slug), the site shows an arbitrary one — this is what causes “old / random versions” to appear. Keep the correct one and delete or re-slug the others from the Content Library. <span className="font-medium text-ink-2">Nothing is deleted automatically.</span></p>
        {dupSlugs.length === 0 ? (
          <div className="flex items-center gap-2 border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> No duplicate addresses. Your content is clean.</div>
        ) : (
          <div className="space-y-4">
            {Object.entries(dupGroups).map(([slug, items]) => (
              <div key={slug} className="border border-red-200 bg-red-50/40">
                <div className="border-b border-red-200 px-4 py-2 text-sm font-medium text-red-700">/{slug} — {items.length} copies</div>
                <table className="w-full text-left text-sm">
                  <tbody>
                    {items.map((d, i) => (
                      <tr key={d.id} className="border-b border-red-100 last:border-0" data-testid={`dup-row-${d.id}`}>
                        <td className="px-4 py-2.5">
                          <span className="text-ink">{d.title}</span>
                          <span className="ml-2 text-xs text-ink-3">{d.type}</span>
                          {i === 0 && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">most recent</span>}
                          {d.hidden && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">hidden</span>}
                          {!d.has_body && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">empty</span>}
                          <span className="block text-xs text-ink-3">updated {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Link to={`/admin/content/${d.id}`} className="text-xs text-saffron hover:underline">Open in editor →</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </section>

      {(data.missing_image || []).length > 0 && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 font-serif-latin text-xl text-ink"><ImageOff className="h-5 w-5 text-amber-500" /> Items with no cover image</h2>
          <p className="mb-4 text-sm text-ink-3">These items fall back to an auto-picked artwork (the “random image” you noticed). Set a cover image in the editor to control it.</p>
          <div className="flex flex-wrap gap-2">
            {data.missing_image.map((d) => (
              <Link key={d.id} to={`/admin/content/${d.id}`} className="border border-line bg-surface px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron">{d.title} <span className="text-ink-3">· {d.type}</span></Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function DbTab() {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState(null);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const limit = 25;

  useEffect(() => { adminDbCollections().then((d) => setCollections(d.collections || [])).catch(() => {}); }, []);

  const loadDocs = (name, nextSkip = 0, query = "") => {
    setBusy(true);
    adminDbDocuments(name, { skip: nextSkip, limit, q: query || undefined })
      .then((d) => { setDocs(d.documents || []); setTotal(d.total || 0); setSkip(nextSkip); })
      .finally(() => setBusy(false));
  };

  const openCollection = (name) => { setActive(name); setQ(""); loadDocs(name, 0, ""); };

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
      <aside className="border border-line bg-surface">
        <p className="border-b border-line px-4 py-3 text-xs uppercase tracking-wider text-ink-3">Collections</p>
        <div className="max-h-[70vh] overflow-y-auto">
          {collections.map((c) => (
            <button key={c.name} data-testid={`db-coll-${c.name}`} onClick={() => openCollection(c.name)}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-surface-alt ${active === c.name ? "bg-surface-alt text-ink" : "text-ink-2"}`}>
              <span>{c.name}</span><span className="text-xs text-ink-3">{c.count}</span>
            </button>
          ))}
        </div>
      </aside>

      <div className="min-w-0">
        {!active ? (
          <div className="flex h-full items-center justify-center border border-line bg-surface p-16 text-sm text-ink-3">Select a collection to browse (read-only).</div>
        ) : (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <h3 className="font-serif-latin text-xl text-ink">{active} <span className="text-sm text-ink-3">· {total} docs</span></h3>
              <div className="ml-auto flex items-center gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-ink-3" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadDocs(active, 0, q)} placeholder="Search slug / title / name / email" className="border border-line bg-parchment py-2 pl-8 pr-3 text-sm focus:border-saffron focus:outline-none" data-testid="db-search" />
                </div>
              </div>
            </div>
            {busy ? <p className="py-16 text-center text-ink-3">…</p> : (
              <>
                <div className="space-y-3">
                  {docs.map((d) => (
                    <details key={d._id} className="border border-line bg-surface" data-testid={`db-doc-${d._id}`}>
                      <summary className="cursor-pointer px-4 py-2.5 text-sm text-ink-2">
                        <span className="font-mono text-xs text-ink-3">{d._id}</span>
                        {d.title || d.name || d.slug || d.email ? <span className="ml-3 text-ink">{d.title || d.name || d.slug || d.email}</span> : null}
                      </summary>
                      <pre className="max-h-96 overflow-auto border-t border-line bg-parchment/40 p-4 text-xs text-ink-2">{JSON.stringify(d, null, 2)}</pre>
                    </details>
                  ))}
                  {docs.length === 0 && <p className="py-16 text-center text-ink-3">No documents.</p>}
                </div>
                {total > limit && (
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <button disabled={skip === 0} onClick={() => loadDocs(active, Math.max(0, skip - limit), q)} className="border border-line px-3 py-1.5 disabled:opacity-40">Previous</button>
                    <span className="text-ink-3">{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
                    <button disabled={skip + limit >= total} onClick={() => loadDocs(active, skip + limit, q)} className="border border-line px-3 py-1.5 disabled:opacity-40">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminData() {
  const [tab, setTab] = useState("health");
  return (
    <div className="max-w-6xl">
      <h1 className="mb-1 font-serif-latin text-3xl text-ink">Data & Health</h1>
      <p className="mb-6 text-sm text-ink-3">Diagnose duplicate/old versions and browse the live database (read-only).</p>
      <div className="mb-6 flex gap-1 border-b border-line">
        {[["health", "Content Health"], ["db", "Database"]].map(([k, l]) => (
          <button key={k} data-testid={`data-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === k ? "border-saffron text-ink" : "border-transparent text-ink-3 hover:text-ink-2"}`}>
            {k === "health" ? <AlertTriangle className="h-4 w-4" /> : <Database className="h-4 w-4" />} {l}
          </button>
        ))}
      </div>
      {tab === "health" ? <HealthTab /> : <DbTab />}
    </div>
  );
}
